<template>
  <b-container fluid class="event-notification-rule-page py-3">
    <div class="iq-card">
      <div class="iq-card-header d-flex align-items-center justify-content-between">
        <div class="iq-header-title py-2">
          <h4 class="card-title mb-0">Event Notification Rule</h4>
        </div>
        <div class="header-actions">
          <b-button variant="outline-secondary" size="sm" :to="{ name: 'MasterConfiguration' }">
            Back
          </b-button>
          <b-button variant="primary" size="sm" :disabled="isLoading" @click="fetchList">
            <i v-if="isLoading && activeMethod === 'GET'" class="ri-loader-line mr-1"></i>
            Refresh
          </b-button>
        </div>
      </div>

      <div class="iq-card-body">
        <b-alert v-if="message.text" show :variant="message.variant" class="mb-3">
          {{ message.text }}
        </b-alert>

        <b-row>
          <b-col lg="12" class="mb-3">
            <section class="panel">
              <div class="table-toolbar">
                <div>
                  <h5 class="panel-title mb-1">Assets</h5>
                  <small class="text-muted">{{ totalData }} assets</small>
                </div>
                <div class="table-actions">
                  <b-button variant="success" size="sm" :disabled="isLoading" @click="openAddRuleModal">
                    Add Rule
                  </b-button>
                  <div class="pagination-control">
                    <b-form-select
                      v-model="pagination.itemPerPage"
                      :options="pageSizeOptions"
                      size="sm"
                      :disabled="isLoading"
                      @change="onPageSizeChanged"
                    />
                  </div>
                </div>
              </div>

              <div class="filter-panel">
                <b-row>
                  <b-col md="6" xl="3" class="mb-2">
                    <b-form-group label="Asset" class="mb-0">
                      <searchable-select
                        v-model="filters.asset"
                        :fetcher="searchAssets"
                        placeholder="All assets"
                        :disabled="isLoading"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>

                <div class="filter-actions">
                  <b-button variant="outline-secondary" size="sm" :disabled="isLoading" @click="resetFilters">
                    Reset
                  </b-button>
                  <b-button variant="primary" size="sm" :disabled="isLoading" @click="applyFilters">
                    Apply Filter
                  </b-button>
                </div>
              </div>

              <b-table
                responsive
                hover
                bordered
                small
                :busy="isLoading && activeMethod === 'GET'"
                :items="items"
                :fields="tableFields"
                tbody-tr-class="asset-row"
                show-empty
                empty-text="No asset data."
                @row-clicked="toggleAssetDetails"
              >
                <template #table-busy>
                  <div class="text-center py-4">
                    <b-spinner small class="mr-2"></b-spinner>
                    Loading...
                  </div>
                </template>

                <template #cell(asset)="row">
                  <div class="asset-cell">
                    <div class="cell-title asset-title">
                      <i :class="row.detailsShowing ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'"></i>
                      <span>{{ getAssetLabel(row.item) }}</span>
                    </div>
                    <b-button
                      variant="outline-primary"
                      size="sm"
                      :disabled="isLoading || !row.item.rules.length"
                      @click.stop="openDuplicateAssetToAssetsModal(row.item)"
                    >
                      Duplicate
                    </b-button>
                  </div>
                </template>

                <template #cell(ruleCount)="row">
                  <b-badge variant="info">{{ getRuleGroups(row.item).length }}</b-badge>
                </template>

                <template #cell(ruleSummary)="row">
                  <div v-if="getRuleGroups(row.item).length" class="event-summary-list">
                    <span
                      v-for="group in getRuleGroups(row.item)"
                      :key="group.key"
                      class="event-summary-item"
                    >
                      <span>{{ group.eventName }}</span>
                      <b-badge :variant="getEventPriorityVariant(group.event)" class="priority-badge">
                        {{ getEventPriorityLabel(group.event) }}
                      </b-badge>
                    </span>
                  </div>
                  <span v-else class="text-muted">No rule for this asset.</span>
                </template>

                <template #row-details="row">
                  <div class="event-rule-detail">
                    <div class="detail-toolbar">
                      <div class="detail-selection">
                        <b-form-checkbox
                          :checked="isAllAssetRulesSelected(row.item)"
                          :indeterminate="isSomeAssetRulesSelected(row.item)"
                          :disabled="isLoading || !row.item.rules.length"
                          @change="toggleAssetRuleSelection(row.item, $event)"
                        >
                          <small class="text-muted">
                            {{ getSelectedRuleCount(row.item) }} selected / {{ getRuleGroups(row.item).length }} events
                          </small>
                        </b-form-checkbox>
                      </div>
                      <div class="row-actions">
                        <b-button
                          variant="outline-danger"
                          size="sm"
                          :disabled="isLoading || !getSelectedRuleCount(row.item)"
                          @click.stop="confirmBulkDelete(row.item)"
                        >
                          Delete Selected
                        </b-button>
                        <b-button
                          variant="outline-success"
                          size="sm"
                          :disabled="isLoading"
                          @click.stop="openAddRuleModal(row.item)"
                        >
                          Add Rule
                        </b-button>
                      </div>
                    </div>

                    <div v-if="getRuleGroups(row.item).length" class="asset-rule-list">
                      <div
                        v-for="group in getRuleGroups(row.item)"
                        :key="group.key"
                        class="asset-rule-group"
                      >
                        <button
                          type="button"
                          class="asset-rule-header"
                          @click.stop="toggleEventGroup(row.item, group.key)"
                        >
                          <span class="asset-title">
                            <i :class="isEventGroupOpen(row.item, group.key) ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'"></i>
                            <span class="cell-title">{{ group.eventName }}</span>
                            <b-badge :variant="getEventPriorityVariant(group.event)" class="priority-badge">
                              {{ getEventPriorityLabel(group.event) }}
                            </b-badge>
                          </span>
                          <span class="event-group-meta">
                            <small class="text-muted">{{ group.rules.length }} recipients</small>
                            <b-form-checkbox
                              :checked="isAllEventRulesSelected(group)"
                              :indeterminate="isSomeEventRulesSelected(group)"
                              :disabled="isLoading || !group.rules.length"
                              @click.stop
                              @change="toggleEventRuleSelection(group, $event)"
                            />
                            <b-button
                              variant="outline-primary"
                              size="sm"
                              :disabled="isLoading"
                              @click.stop="openDuplicateEventToEventsModal(row.item, group.event)"
                            >
                              Duplicate
                            </b-button>
                          </span>
                        </button>

                        <div v-show="isEventGroupOpen(row.item, group.key)" class="rule-list">
                          <div
                            v-for="rule in group.rules"
                            :key="rule.enruId"
                            class="rule-item"
                          >
                            <div class="rule-main">
                              <div class="rule-select-cell">
                                <b-form-checkbox
                                  :checked="isRuleSelected(rule)"
                                  :disabled="isLoading"
                                  @change="toggleRuleSelection(rule, $event)"
                                />
                              </div>
                              <div>
                                <div class="recipient-inline">
                                  <span class="cell-title">{{ getRecipientName(rule) }}</span>
                                  <small class="text-muted">{{ getRecipientEmail(rule) }}</small>
                                </div>
                              </div>
                              <div>
                                <b-badge class="type-badge">{{ rule.enruRecipientType }}</b-badge>
                              </div>
                              <div>
                                <small class="text-muted">{{ rule.enruUpdatedTime || '-' }}</small>
                              </div>
                            </div>
                            <div class="row-actions">
                              <b-button
                                variant="outline-primary"
                                size="sm"
                                :disabled="isLoading"
                                @click="openEditRuleModal(rule)"
                              >
                                Edit
                              </b-button>
                              <b-button
                                variant="outline-danger"
                                size="sm"
                                :disabled="isLoading"
                                @click="confirmDelete(rule)"
                              >
                                Delete
                              </b-button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div v-else class="text-muted">No rule for this asset.</div>
                  </div>
                </template>

              </b-table>

              <div class="d-flex justify-content-end mt-3">
                <b-pagination
                  v-model="pagination.page"
                  :total-rows="pagination.totalData"
                  :per-page="pagination.itemPerPage"
                  size="sm"
                  :disabled="isLoading"
                  @change="onPageChanged"
                />
              </div>
            </section>
          </b-col>
        </b-row>
      </div>
    </div>

    <b-modal
      v-model="deleteModalVisible"
      title="Delete event notification rule"
      ok-title="Delete"
      ok-variant="danger"
      :ok-disabled="isLoading"
      :cancel-disabled="isLoading"
      @ok.prevent="deleteRule"
    >
      <p class="mb-2">
        Delete rule <strong>#{{ selectedRule.enruId }}</strong>?
      </p>
      <div class="text-muted">
        {{ getEventType(selectedRule) }} - {{ getRecipientName(selectedRule) }}
      </div>
    </b-modal>

    <b-modal
      v-model="bulkDeleteModalVisible"
      title="Delete selected rules"
      ok-title="Delete"
      ok-variant="danger"
      :ok-disabled="isLoading || !selectedRuleIds.length"
      :cancel-disabled="isLoading"
      @ok.prevent="deleteSelectedRules"
    >
      <p class="mb-2">
        Delete <strong>{{ selectedRuleIds.length }}</strong> selected rules?
      </p>
      <div class="text-muted">
        This action will remove the selected notification rules.
      </div>
    </b-modal>

    <b-modal
      v-model="addModalVisible"
      :title="isEditing ? 'Edit event notification rule' : 'Add event notification rule'"
      hide-footer
      lazy
      no-close-on-backdrop
      no-close-on-esc
    >
      <b-form @submit.prevent="createRule">
        <b-form-group label="Event">
          <searchable-select
            v-model="form.event"
            :fetcher="searchEvents"
            placeholder="Select event"
            :disabled="isLoading"
            :multiple="!isEditing"
          />
        </b-form-group>

        <b-form-group label="Asset">
          <searchable-select
            v-model="form.asset"
            :fetcher="searchAssets"
            placeholder="Select asset"
            :disabled="isLoading"
          />
        </b-form-group>

        <b-form-group label="Recipient">
          <searchable-select
            v-model="form.recipient"
            :fetcher="searchRecipients"
            placeholder="Select recipient"
            :disabled="isLoading"
            :multiple="!isEditing"
          />
        </b-form-group>

        <b-form-group label="Recipient Type">
          <v-select
            v-model="form.enruRecipientType"
            :options="recipientTypeOptions"
            label="text"
            :reduce="(option) => option.value"
            placeholder="Select type"
            :disabled="isLoading"
            class="searchable-select"
          />
        </b-form-group>

        <div class="modal-actions">
          <b-button variant="outline-secondary" :disabled="isLoading" @click="closeAddRuleModal">
            Cancel
          </b-button>
          <b-button type="submit" variant="success" :disabled="isLoading || !canSubmit">
            <i v-if="isLoading && ['POST', 'PUT'].includes(activeMethod)" class="ri-loader-line mr-1"></i>
            Save
          </b-button>
        </div>
      </b-form>
    </b-modal>

    <b-modal
      v-model="duplicateModalVisible"
      title="Duplicate rules"
      ok-title="Duplicate"
      ok-variant="primary"
      :ok-disabled="isLoading || !canDuplicate"
      :cancel-disabled="isLoading"
      @ok.prevent="duplicateSelectedRules"
    >
      <div class="mb-3">
        <div class="cell-title">{{ duplicateSourceTitle }}</div>
        <small class="text-muted">{{ duplicateSourceDescription }}</small>
      </div>

      <template v-if="duplicateMode === 'asset_to_assets'">
        <b-form-group label="Target Assets" class="mb-0">
          <searchable-select
            v-model="duplicateForm.targetAssets"
            :fetcher="searchDuplicateTargetAssets"
            placeholder="Select target assets"
            :disabled="isLoading"
            multiple
          />
        </b-form-group>

        <small class="text-muted d-block mt-2">
          Existing target rules with the same event and recipient will be skipped.
        </small>
      </template>

      <template v-else>
        <b-form-group label="Target Events" class="mb-0">
          <searchable-select
            v-model="duplicateForm.targetEvents"
            :fetcher="searchDuplicateTargetEvents"
            placeholder="Select target events"
            :disabled="isLoading"
            multiple
          />
        </b-form-group>

        <small class="text-muted d-block mt-2">
          Existing target rules with the same event and recipient will be skipped.
        </small>
      </template>
    </b-modal>
  </b-container>
</template>

<script>
import AssetSvc from '@/services/AssetSvc';
import EventSvc from '@/services/EventSvc';
import EventNotificationRuleSvc from '@/services/EventNotificationRuleSvc';
import NotificationRecipientSvc from '@/services/NotificationRecipientSvc';
import SearchableSelect from '@/components/master/SearchableSelect.vue';
import apiCrudMixin from '@/mixins/apiCrudMixin';

const SEARCH_RESULT_LIMIT = 50;
const RULE_RESULT_LIMIT = 1000;

export default {
  name: 'EventNotificationRule',
  components: {
    SearchableSelect,
  },
  mixins: [apiCrudMixin],
  data() {
    return {
      addModalVisible: false,
      bulkDeleteModalVisible: false,
      deleteModalVisible: false,
      duplicateModalVisible: false,
      duplicateMode: 'asset_to_assets',
      duplicateForm: {
        sourceAsset: null,
        sourceEvent: null,
        targetAssets: [],
        targetEvents: [],
      },
      editingId: null,
      form: {
        event: [],
        asset: null,
        recipient: [],
        enruRecipientType: 'to',
      },
      filters: {
        asset: null,
      },
      recipientTypeOptions: [
        { value: 'to', text: 'To' },
        { value: 'cc', text: 'Cc' },
        { value: 'bcc', text: 'Bcc' },
      ],
      selectedRule: {},
      selectedRuleMap: {},
      tableFields: [
        { key: 'asset', label: 'Asset', thStyle: { width: '240px' } },
        { key: 'ruleCount', label: 'Events', thStyle: { width: '80px' } },
        { key: 'ruleSummary', label: 'Events' },
      ],
    };
  },
  computed: {
    canSubmit() {
      return Boolean(
        this.hasSelectedOptions(this.form.event)
        && this.form.asset
        && this.hasSelectedOptions(this.form.recipient)
        && this.form.enruRecipientType,
      );
    },
    isEditing() {
      return this.editingId !== null;
    },
    selectedRuleIds() {
      return Object.keys(this.selectedRuleMap).filter((id) => this.selectedRuleMap[id]);
    },
    canDuplicate() {
      if (this.duplicateMode === 'asset_to_assets') {
        return Boolean(
          this.duplicateForm.sourceAsset
          && this.duplicateForm.targetAssets.length,
        );
      }

      return Boolean(
        this.duplicateForm.sourceAsset
        && this.duplicateForm.sourceEvent
        && this.duplicateForm.targetEvents.length,
      );
    },
    duplicateSourceTitle() {
      if (this.duplicateMode === 'asset_to_assets') {
        return this.duplicateForm.sourceAsset ? this.duplicateForm.sourceAsset.text : 'Asset to assets';
      }

      return this.duplicateForm.sourceEvent ? this.duplicateForm.sourceEvent.text : 'Event to events';
    },
    duplicateSourceDescription() {
      if (this.duplicateMode === 'asset_to_assets') {
        const assetText = this.duplicateForm.sourceAsset ? this.duplicateForm.sourceAsset.text : '-';
        return `Copy all events and recipients from ${assetText} to target assets.`;
      }

      const eventText = this.duplicateForm.sourceEvent ? this.duplicateForm.sourceEvent.text : '-';
      const assetText = this.duplicateForm.sourceAsset ? this.duplicateForm.sourceAsset.text : '-';
      return `Copy all recipients from ${eventText} to target events in ${assetText}.`;
    },
  },
  mounted() {
    this.fetchList();
  },
  methods: {
    applyFilters() {
      this.pagination.page = 1;
      this.fetchList();
    },
    buildSearchParams(field, query) {
      const params = { max: SEARCH_RESULT_LIMIT };
      if (query) {
        params[`finds[${field}]`] = query;
      }
      return params;
    },
    buildCreateRulePayloads() {
      const events = this.normalizeSelectedOptions(this.form.event);
      const recipients = this.normalizeSelectedOptions(this.form.recipient);
      const assetId = this.form.asset ? this.form.asset.value : null;
      const payloads = [];

      events.forEach((event) => {
        recipients.forEach((recipient) => {
          payloads.push({
            enruMevtId: event.value,
            enruMassId: assetId,
            enruNtrcId: recipient.value,
            enruRecipientType: this.form.enruRecipientType,
          });
        });
      });

      return payloads;
    },
    closeAddRuleModal() {
      this.addModalVisible = false;
    },
    closeDuplicateModal() {
      this.duplicateModalVisible = false;
      this.duplicateForm = this.getDefaultDuplicateForm();
    },
    confirmDelete(rule) {
      this.selectedRule = rule;
      this.deleteModalVisible = true;
    },
    confirmBulkDelete() {
      this.bulkDeleteModalVisible = true;
    },
    async createRule() {
      const method = this.isEditing ? 'PUT' : 'POST';
      const requestFn = this.isEditing
        ? () => EventNotificationRuleSvc.update(this.editingId, {
          enruMevtId: this.form.event ? this.form.event.value : null,
          enruMassId: this.form.asset ? this.form.asset.value : null,
          enruNtrcId: this.form.recipient ? this.form.recipient.value : null,
          enruRecipientType: this.form.enruRecipientType,
        })
        : () => Promise.all(this.buildCreateRulePayloads().map(
          (payload) => EventNotificationRuleSvc.create(payload),
        ));

      const successMessage = this.isEditing
        ? 'Rule updated.'
        : `${this.buildCreateRulePayloads().length} rules saved.`;

      await this.runRequest(method, requestFn, async () => {
        this.resetForm();
        this.addModalVisible = false;
        this.setMessage(successMessage);
        await this.fetchList(false);
      });
    },
    async deleteRule() {
      const id = this.selectedRule.enruId;
      await this.runRequest(
        'DELETE',
        () => EventNotificationRuleSvc.delete(id),
        async () => {
          this.deleteModalVisible = false;
          this.selectedRule = {};
          this.setMessage('Rule deleted.');
          await this.fetchList(false);
        },
      );
    },
    async deleteSelectedRules() {
      const ids = this.selectedRuleIds;
      await this.runRequest(
        'DELETE',
        () => Promise.all(ids.map((id) => EventNotificationRuleSvc.delete(id))),
        async () => {
          this.bulkDeleteModalVisible = false;
          this.selectedRuleMap = {};
          this.setMessage(`${ids.length} rules deleted.`);
          await this.fetchList(false);
        },
      );
    },
    async duplicateSelectedRules() {
      if (this.duplicateMode === 'asset_to_assets') {
        await this.duplicateAssetToAssets();
        return;
      }

      await this.duplicateEventToEvents();
    },
    async duplicateAssetToAssets() {
      const sourceAssetId = this.duplicateForm.sourceAsset.value;
      const targetAssetIds = this.duplicateForm.targetAssets.map((asset) => asset.value);

      await this.runRequest('POST', async () => {
        const sourceResponse = await EventNotificationRuleSvc.get({
          page: 1,
          max: RULE_RESULT_LIMIT,
          'finds[enruMassId]': sourceAssetId,
        });
        const sourceRules = this.getRows(sourceResponse);
        let duplicatedCount = 0;
        let skippedCount = 0;

        await Promise.all(targetAssetIds.map(async (targetAssetId) => {
          const targetResponse = await EventNotificationRuleSvc.get({
            page: 1,
            max: RULE_RESULT_LIMIT,
            'finds[enruMassId]': targetAssetId,
          });
          const targetRules = this.getRows(targetResponse);
          const existingKeys = new Set(
            targetRules.map((rule) => this.getDuplicateRuleKey(rule.enruMevtId, rule.enruNtrcId)),
          );
          const rulesToCreate = sourceRules.filter((rule) => {
            const key = this.getDuplicateRuleKey(rule.enruMevtId, rule.enruNtrcId);
            if (existingKeys.has(key)) {
              return false;
            }

            existingKeys.add(key);
            return true;
          });

          skippedCount += sourceRules.length - rulesToCreate.length;
          duplicatedCount += rulesToCreate.length;

          await Promise.all(rulesToCreate.map((rule) => EventNotificationRuleSvc.create({
            enruMevtId: rule.enruMevtId,
            enruMassId: targetAssetId,
            enruNtrcId: rule.enruNtrcId,
            enruRecipientType: rule.enruRecipientType,
          })));
        }));

        return { duplicatedCount, skippedCount };
      }, async (result) => {
        this.closeDuplicateModal();
        this.setMessage(
          `Duplicated ${result.duplicatedCount} rules. Skipped ${result.skippedCount} existing event/recipient pairs.`,
        );
        await this.fetchList(false);
      });
    },
    async duplicateEventToEvents() {
      const sourceEventId = this.duplicateForm.sourceEvent.value;
      const sourceAssetId = this.duplicateForm.sourceAsset.value;
      const targetEventIds = this.duplicateForm.targetEvents.map((event) => event.value);

      await this.runRequest('POST', async () => {
        const sourceResponse = await EventNotificationRuleSvc.get({
          page: 1,
          max: RULE_RESULT_LIMIT,
          'finds[enruMassId]': sourceAssetId,
          'finds[enruMevtId]': sourceEventId,
        });
        const sourceRules = this.getRows(sourceResponse);
        let duplicatedCount = 0;
        let skippedCount = 0;

        await Promise.all(targetEventIds.map(async (targetEventId) => {
          const targetResponse = await EventNotificationRuleSvc.get({
            page: 1,
            max: RULE_RESULT_LIMIT,
            'finds[enruMassId]': sourceAssetId,
            'finds[enruMevtId]': targetEventId,
          });
          const targetRules = this.getRows(targetResponse);
          const existingKeys = new Set(
            targetRules.map((rule) => this.getDuplicateRuleKey(rule.enruMevtId, rule.enruNtrcId)),
          );
          const rulesToCreate = sourceRules.filter((rule) => {
            const key = this.getDuplicateRuleKey(targetEventId, rule.enruNtrcId);
            if (existingKeys.has(key)) {
              return false;
            }

            existingKeys.add(key);
            return true;
          });

          skippedCount += sourceRules.length - rulesToCreate.length;
          duplicatedCount += rulesToCreate.length;

          await Promise.all(rulesToCreate.map((rule) => EventNotificationRuleSvc.create({
            enruMevtId: targetEventId,
            enruMassId: sourceAssetId,
            enruNtrcId: rule.enruNtrcId,
            enruRecipientType: rule.enruRecipientType,
          })));
        }));

        return { duplicatedCount, skippedCount };
      }, async (result) => {
        this.closeDuplicateModal();
        this.setMessage(
          `Duplicated ${result.duplicatedCount} rules. Skipped ${result.skippedCount} existing event/recipient pairs.`,
        );
        await this.fetchList(false);
      });
    },
    async fetchList(showLoading = true) {
      this.activeMethod = 'GET';
      this.clearMessage();
      this.isLoading = showLoading;

      try {
        const assetParams = {
          page: this.pagination.page,
          max: this.pagination.itemPerPage,
        };

        if (this.filters.asset) {
          assetParams['finds[massId]'] = this.filters.asset.value;
        }

        const assetResponse = await AssetSvc.getAssets({ params: assetParams });
        const assets = this.getRows(assetResponse);
        const pagination = this.getPagination(assetResponse);
        const ruleResults = await Promise.all(
          assets.map(async (asset) => {
            const response = await EventNotificationRuleSvc.get(
              this.buildRuleListParams(asset.massId),
            );

            return {
              ...asset,
              rules: this.getRows(response),
            };
          }),
        );

        this.items = ruleResults;
        this.selectedRuleMap = {};
        this.pagination = {
          page: pagination.page || this.pagination.page,
          itemPerPage: pagination.itemPerPage || this.pagination.itemPerPage,
          totalData: pagination.totalData || ruleResults.length,
        };
      } catch (error) {
        const response = error.response;
        this.setMessage(
          response && response.data && response.data.status && response.data.status.message
            ? response.data.status.message
            : error.message,
          'danger',
        );
      } finally {
        this.isLoading = false;
        this.activeMethod = '';
      }
    },
    buildRuleListParams(assetId) {
      const params = {
        page: 1,
        max: RULE_RESULT_LIMIT,
      };
      params['finds[enruMassId]'] = assetId;

      return params;
    },
    getRows(response) {
      // Sebagian service mengembalikan axios response penuh ({ data: { data: [...] } }),
      // sebagian (mis. EventSvc) sudah unwrap jadi body ({ data: [...] }).
      const payload = response && response.data;
      if (Array.isArray(payload)) {
        return payload;
      }
      if (payload && Array.isArray(payload.data)) {
        return payload.data;
      }
      return [];
    },
    hasSelectedOptions(value) {
      return Array.isArray(value) ? value.length > 0 : Boolean(value);
    },
    normalizeSelectedOptions(value) {
      if (Array.isArray(value)) {
        return value;
      }

      return value ? [value] : [];
    },
    getPagination(response) {
      const payload = response && response.data && !Array.isArray(response.data)
        ? response.data
        : response;

      return (payload && payload.pagination) || {};
    },
    getAssetLabel(asset) {
      return asset && asset.massName ? asset.massName : '-';
    },
    getAssetName(rule) {
      return rule && rule.enruMass && rule.enruMass.massName ? rule.enruMass.massName : '-';
    },
    getEventLabel(event) {
      return event && event.mevtType ? event.mevtType : '-';
    },
    getEventOptionText(event) {
      return `${this.getEventLabel(event)} - ${this.getEventPriorityLabel(event)}`;
    },
    getEventPriorityLabel(event) {
      return Number(event && event.mevtIsPriority) === 1 ? 'High' : 'Normal';
    },
    getEventPriorityVariant(event) {
      return Number(event && event.mevtIsPriority) === 1 ? 'danger' : 'success';
    },
    getEventType(rule) {
      return rule && rule.enruMevt && rule.enruMevt.mevtType ? rule.enruMevt.mevtType : '-';
    },
    getRuleEventOptionText(rule) {
      return rule && rule.enruMevt
        ? this.getEventOptionText(rule.enruMevt)
        : this.getEventType(rule);
    },
    getRecipientEmail(rule) {
      return rule && rule.enruNtrc && rule.enruNtrc.ntrcEmail ? rule.enruNtrc.ntrcEmail : '';
    },
    getRecipientName(rule) {
      return rule && rule.enruNtrc && rule.enruNtrc.ntrcName ? rule.enruNtrc.ntrcName : '-';
    },
    getDuplicateRuleKey(eventId, recipientId) {
      return `${eventId || ''}:${recipientId || ''}`;
    },
    getRuleIds(rules) {
      return (rules || []).map((rule) => String(rule.enruId)).filter(Boolean);
    },
    getDefaultDuplicateForm() {
      return {
        sourceAsset: null,
        sourceEvent: null,
        targetAssets: [],
        targetEvents: [],
      };
    },
    getRuleSummary(asset) {
      const groups = this.getRuleGroups(asset);
      const firstGroup = groups[0];
      const remainingCount = groups.length - 1;

      if (remainingCount <= 0) {
        return firstGroup.eventName;
      }

      return `${firstGroup.eventName} +${remainingCount} events`;
    },
    getRuleGroups(asset) {
      const groups = {};
      (asset.rules || []).forEach((rule) => {
        const key = rule.enruMevtId || 'unknown';
        if (!groups[key]) {
          groups[key] = {
            key,
            event: {
              ...(rule.enruMevt || {}),
              mevtId: rule.enruMevtId,
            },
            eventName: this.getEventType(rule),
            rules: [],
          };
        }

        groups[key].rules.push(rule);
      });

      return Object.values(groups).sort((a, b) => {
        const aHasRules = a.rules.length > 0;
        const bHasRules = b.rules.length > 0;
        if (aHasRules !== bHasRules) {
          return aHasRules ? -1 : 1;
        }

        return a.eventName.localeCompare(b.eventName);
      });
    },
    getSelectedRuleCount(asset) {
      return this.getRuleIds(asset.rules).filter((id) => this.selectedRuleMap[id]).length;
    },
    isEventGroupOpen(asset, groupKey) {
      return Boolean(asset.openEventGroups && asset.openEventGroups[groupKey]);
    },
    isRuleSelected(rule) {
      return Boolean(this.selectedRuleMap[String(rule.enruId)]);
    },
    isAllAssetRulesSelected(asset) {
      const ruleIds = this.getRuleIds(asset.rules);
      return ruleIds.length > 0 && ruleIds.every((id) => this.selectedRuleMap[id]);
    },
    isSomeAssetRulesSelected(asset) {
      const ruleIds = this.getRuleIds(asset.rules);
      return ruleIds.some((id) => this.selectedRuleMap[id]) && !this.isAllAssetRulesSelected(asset);
    },
    isAllEventRulesSelected(group) {
      const ruleIds = this.getRuleIds(group.rules);
      return ruleIds.length > 0 && ruleIds.every((id) => this.selectedRuleMap[id]);
    },
    isSomeEventRulesSelected(group) {
      const ruleIds = this.getRuleIds(group.rules);
      return ruleIds.some((id) => this.selectedRuleMap[id]) && !this.isAllEventRulesSelected(group);
    },
    toggleAssetDetails(item) {
      if (this.isLoading) return;
      this.$set(item, '_showDetails', !item._showDetails);
    },
    toggleAssetRuleSelection(asset, checked) {
      this.getRuleIds(asset.rules).forEach((id) => {
        this.$set(this.selectedRuleMap, id, checked);
      });
    },
    toggleEventRuleSelection(group, checked) {
      this.getRuleIds(group.rules).forEach((id) => {
        this.$set(this.selectedRuleMap, id, checked);
      });
    },
    toggleEventGroup(asset, groupKey) {
      if (!asset.openEventGroups) {
        this.$set(asset, 'openEventGroups', {});
      }

      this.$set(asset.openEventGroups, groupKey, !this.isEventGroupOpen(asset, groupKey));
    },
    toggleRuleSelection(rule, checked) {
      this.$set(this.selectedRuleMap, String(rule.enruId), checked);
    },
    openAddRuleModal(asset = null, event = null) {
      this.resetForm();
      if (asset) {
        this.form.asset = {
          value: asset.massId,
          text: this.getAssetLabel(asset),
        };
      }
      if (event) {
        this.form.event = [{
          value: event.mevtId,
          text: this.getEventOptionText(event),
        }];
      }
      this.addModalVisible = true;
    },
    openDuplicateAssetToAssetsModal(asset) {
      this.duplicateForm = this.getDefaultDuplicateForm();
      this.duplicateMode = 'asset_to_assets';
      this.duplicateForm.sourceAsset = {
        value: asset.massId,
        text: this.getAssetLabel(asset),
      };
      this.duplicateModalVisible = true;
    },
    openDuplicateEventToEventsModal(asset, event) {
      this.duplicateForm = this.getDefaultDuplicateForm();
      this.duplicateMode = 'event_to_events';
      this.duplicateForm.sourceAsset = {
        value: asset.massId,
        text: this.getAssetLabel(asset),
      };
      this.duplicateForm.sourceEvent = {
        value: event.mevtId,
        text: this.getEventOptionText(event),
      };
      this.duplicateModalVisible = true;
    },
    openEditRuleModal(rule) {
      this.editingId = rule.enruId;
      this.form = {
        event: {
          value: rule.enruMevtId,
          text: this.getRuleEventOptionText(rule),
        },
        asset: {
          value: rule.enruMassId,
          text: this.getAssetName(rule),
        },
        recipient: {
          value: rule.enruNtrcId,
          text: `${this.getRecipientName(rule)} - ${this.getRecipientEmail(rule) || '-'}`,
        },
        enruRecipientType: rule.enruRecipientType,
      };
      this.addModalVisible = true;
    },
    resetForm() {
      this.editingId = null;
      this.form = {
        event: [],
        asset: null,
        recipient: [],
        enruRecipientType: 'to',
      };
    },
    resetFilters() {
      this.filters = {
        asset: null,
      };
      this.pagination.page = 1;
      this.fetchList();
    },
    async searchAssets(query) {
      const response = await AssetSvc.getAssets({ params: this.buildSearchParams('massName', query) });
      return this.getRows(response).map((asset) => ({
        value: asset.massId,
        text: asset.massName || '-',
      }));
    },
    async searchDuplicateTargetAssets(query) {
      const assets = await this.searchAssets(query);
      if (!this.duplicateForm.sourceAsset) return assets;

      return assets.filter((asset) => asset.value !== this.duplicateForm.sourceAsset.value);
    },
    async searchDuplicateTargetEvents(query) {
      const events = await this.searchEvents(query);
      if (!this.duplicateForm.sourceEvent) return events;

      return events.filter((event) => event.value !== this.duplicateForm.sourceEvent.value);
    },
    async searchEvents(query) {
      const response = await EventSvc.get(this.buildSearchParams('mevtType', query));
      return this.getRows(response).map((event) => ({
        value: event.mevtId,
        text: this.getEventOptionText(event),
      }));
    },
    async searchRecipients(query) {
      const response = await NotificationRecipientSvc.get(this.buildSearchParams('ntrcName', query));
      return this.getRows(response).map((recipient) => ({
        value: recipient.ntrcId,
        text: `${recipient.ntrcName || '-'} - ${recipient.ntrcEmail || '-'}`,
      }));
    },
  },
};
</script>

<style src="@/views/master/_apiPanel.css" scoped></style>

<style scoped>
.filter-panel {
  border: 1px solid #eef1f5;
  border-radius: 8px;
  margin-bottom: 14px;
  padding: 12px;
  background: #fbfcfd;
}

.filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.cell-title {
  font-weight: 600;
}

.event-notification-rule-page ::v-deep .asset-row {
  cursor: pointer;
}

.asset-cell {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.event-title {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.priority-badge {
  font-size: 10px;
  font-weight: 600;
  padding: 3px 6px;
}

.event-summary-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.event-summary-item {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 6px;
  border: 1px solid #eef1f5;
  border-radius: 4px;
  background: #fbfcfd;
  color: #495057;
  font-size: 12px;
}

.event-rule-detail {
  background: #fbfcfd;
  border: 1px solid #eef1f5;
  border-radius: 6px;
  padding: 8px 10px;
}

.detail-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
}

.detail-selection {
  min-width: 180px;
}

.asset-rule-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.asset-rule-group {
  border: 1px solid #eef1f5;
  border-radius: 6px;
  background: #fff;
  overflow: hidden;
}

.asset-rule-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  padding: 7px 8px;
  background: #f5f7fa;
  border-bottom: 1px solid #e4e8ef;
  border-left: 0;
  border-right: 0;
  border-top: 0;
  color: #212529;
  cursor: pointer;
  text-align: left;
}

.asset-title {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.event-group-meta {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.rule-list {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.rule-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0;
  border-top: 1px solid #eef1f5;
  background: #fff;
}

.rule-item:first-child {
  border-top: none;
}

.rule-main {
  display: grid;
  grid-template-columns: 40px minmax(180px, 1.2fr) 70px minmax(120px, 0.8fr);
  gap: 0;
  align-items: center;
  flex: 1;
}

.rule-main > div {
  min-height: 38px;
  padding: 7px 8px;
  border-right: 1px solid #eef1f5;
}

.rule-select-cell {
  display: flex;
  align-items: center;
  justify-content: center;
}

.recipient-inline {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
  white-space: nowrap;
}

.recipient-inline .cell-title,
.recipient-inline small {
  overflow: hidden;
  text-overflow: ellipsis;
}

.rule-item .row-actions {
  min-height: 38px;
  padding: 6px 8px;
  border-left: 1px solid #eef1f5;
}

.type-badge {
  background-color: #d1ecf1 !important;
  color: #0c5460 !important;
  font-weight: 600;
  font-size: 11px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 3px 7px;
}

.searchable-select {
  width: 100%;
  background-color: #fff;
  border-radius: 4px;
}

@media (max-width: 991.98px) {
  .rule-item,
  .rule-main {
    display: block;
  }

  .rule-main > div {
    margin-bottom: 8px;
  }
}
</style>
